import React, {
  Dispatch,
  forwardRef,
  SetStateAction, useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Accordion } from "react-bootstrap";
import { useFormik } from "formik";
import {
  ButtonBox,
  GridTable,
  LoadingBox,
  ModelDialogBox,
  PaymentDetails,
  PrivilegesChecker,
  RefundItemList,
  RegisterTransactionHeader,
  RegisterTransactionItem,
  TextBox,
} from "../..";
import {
  ActionTypeEnum,
  ComponentActionTypeEnum,
  DailyTransactionTypeEnum,
  HasFormIdModel,
  PaymentTypeEnum,
  PaymentValueDetailsModel,
  RequestActionModel,
  RowStateEnum,
  TransactionDetailResponseModel,
  TransactionItemResponseModel,
  UserConfigurationModel,
  ValidationErrorModel,
} from "../../../models";
import {
  generateGuid,
  getLabelName,
  getPageNameByUrl,
  isArabicCurrentLanguage,
} from "../../../utils";
import { saveTransaction } from "../../../serviceBroker/transactionApiServiceBroker";
import {
  addItem,
  getAccountBalance,
  getCompanySettings,
  handleTransactionTotalValues,
  handleUpdateExistingTransactionValues,
  initializeTransactionValuesAndUserTransactionsConfiguration,
  isRefundTransaction,
  transactionDetailInitialValue,
  transactionItemInitialValues,
  UpdateTransactionActionUser,
} from "../common/businessLogic/transactionBl";
import {
  validateAddItem,
  validateRefundSubmitRequestAdditionalValidation,
  validateSubmitRequest,
} from "../common/businessLogic/validationBl";
import { handleGroupItemsBasedOnId } from "../common/businessLogic/commonBl";
import {
  getTransactionItemsColumns,
  handleRowStyle,
} from "../common/uiHelper/dataTableColumns";
import { useTransaction } from "../../../hooks";
import { UploadedFileModel } from "../../../models";
import _ from "lodash";

interface RegisterTransactionProps extends HasFormIdModel {
  transactionType: DailyTransactionTypeEnum;
  request?: TransactionDetailResponseModel | null;
  setActionType: Dispatch<SetStateAction<ComponentActionTypeEnum>>;
  actionType: ComponentActionTypeEnum;
  onActionEvent: (o: RequestActionModel) => void;
}

export const RegisterTransaction = forwardRef(
  (
    {
      transactionType,
      request,
      setActionType,
      actionType,
      onActionEvent,
      formId,
    }: RegisterTransactionProps,
    ref // Forwarded ref
  ) => {
    //#region state
    const [isArabic] = useState<boolean>(isArabicCurrentLanguage());
    const [, setGuidKey] = useState<string>("");
    const [transactionTypeState, setTransactionTypeState] =
      useState<DailyTransactionTypeEnum>(transactionType);
    const [showRefundItemModel, setShowRefundItemModel] = useState(false);
    const [showPaymentDetailsModel, setShowPaymentDetailsModel] =
      useState(false);
    const [loading, setLoading] = useState(false);
    const [initialValues] = useState<TransactionDetailResponseModel>(
      request ?? transactionDetailInitialValue()
    );
    const [data, setData] = useState<TransactionDetailResponseModel>(
      request || initialValues
    );
    const [refundTransactionData, setRefundTransactionData] =
      useState<TransactionDetailResponseModel>(initialValues);
    const [transactionItemObject, setTransactionItemObject] =
      useState<TransactionItemResponseModel>(transactionItemInitialValues);
    const [pageName, setPageName] = useState<string>("");
    const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
    const [isVisible, setIsVisible] = useState(true);
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFileModel[]>([]);
    const {
      beneficiaryList,
      setBeneficiaryList,
      categoryList,
      itemsPerCategoryList,
      setItemPerCategoryList,
      itemList,
      setItemList,
      paymentTypeList,
      currencyList,
      storeEmployeeList,
      costCenterList,
      calcTypeList,
      storeList,
      userConfiguration,
      isSalesComponent,
      isTransactionLoaded,
      hasSingleStore,
      defaultStoreId,
    } = useTransaction(transactionType, setLoading, data, setData);
    const setDataCallback = useCallback((newData: TransactionDetailResponseModel) => {
      setData(newData);
    }, []);

    const columns = useMemo(() => {
      if (!isTransactionLoaded) return [];
      return [
        ...getTransactionItemsColumns(
          isArabic,
          transactionType,
          setData,
          data,
          getCompanySettings(),
          userConfiguration.userTransactionSetting,
          userConfiguration.userSetting
        ),
      ];
    }, [
      isArabic,
      transactionType,
      setDataCallback,data,
      userConfiguration.userTransactionSetting,
      isTransactionLoaded,
    ]);
    const activeTransactionDetails = useMemo(() => {
      return data.TransactionDetaillist.filter(
        (transaction) => transaction.rowState !== RowStateEnum.Delete
      );
    }, [ JSON.stringify(data.TransactionDetaillist.map(item => ({
      rowKey: item.rowKey,
      rowState: item.rowState,
      id: item.ID,
        Quantity:item.Quantity,
      priceInCurrency:item.Unit_PriceInCurrencyCalculated,
      priceWithTaxInCurrency:item.UnitPriceWithTaxInCurrency,
      priceCost:item.PriceCost,
      itemDiscountInCurrency:item.ItemDiscountInCurrency
    })))]);
    //#endregion
    //#region ref
    const StoreSelectBoxMultiselectRef = useRef<any>();
    const itemCategorySelectBoxMultiselectRef = useRef<any>();
    const itemSelectBoxMultiselectRef = useRef<any>();
    const customerSelectBoxMultiselectRef = useRef<any>();
    const calcTypeSelectBoxMultiselectRef = useRef<any>();
    const paymentTypeSelectBoxMultiselectRef = useRef<any>();
    const paymentTypeForSupplierPaymentSelectBoxMultiselectRef = useRef<any>();
    const currencySelectBoxMultiselectRef = useRef<any>();
    const storeEmployeeSelectBoxMultiselectRef = useRef<any>();
    const costCenterSelectBoxMultiselectRef = useRef<any>();
    const transactionSettingStatusButtonRef = useRef<any>();
    //#endregion
    //#region Public Method
    useImperativeHandle(ref, () => ({
      clearData: () => {
        setData(transactionDetailInitialValue()); // Reset the state to initial values
      },
      fillData: async (newData: TransactionDetailResponseModel) => {
        await handleClearComponentControls();
        setTransactionItemObject({ ...transactionItemInitialValues });
        setData(newData);
        //updateAllSelectBoxes(newData);
      },
    }));
    //#endregion
    //#region function
    const updateAllSelectBoxes = (request: TransactionDetailResponseModel) => {
      const selectBoxMappings = [
        {
          ref: costCenterSelectBoxMultiselectRef,
          value: request.CostCenter_ID,
        },
        { ref: customerSelectBoxMultiselectRef, value: request.Customer_ID },
        { ref: currencySelectBoxMultiselectRef, value: request.Currency_ID },
        { ref: storeEmployeeSelectBoxMultiselectRef, value: request.Emp_ID },
        {
          ref: paymentTypeSelectBoxMultiselectRef,
          value: request.PaymentType_ID,
        },
        {
          ref: paymentTypeForSupplierPaymentSelectBoxMultiselectRef,
          value: request.PaymentTypeIdForSupplier,
        },
        { ref: calcTypeSelectBoxMultiselectRef, value: request.CalcType_ID },
      ];
      selectBoxMappings.forEach(({ ref, value }) => {
        if (value != null) {
          ref.current?.setValue(value);
        } else {
          ref.current?.clearValue();
        }
      });
    };

    const handleAction = async (request: RequestActionModel) => {
      switch (request.action) {
        case ActionTypeEnum.AddPaymentValuesSuccess:
          setShowPaymentDetailsModel(false);
          const paymentDetails: PaymentValueDetailsModel =
            request.request as PaymentValueDetailsModel;
          let dataObjet: TransactionDetailResponseModel = {
            ...data,
            CashMoney: paymentDetails.cash,
            CardMoney: paymentDetails.credit,
            DelayMoney: paymentDetails.delay,
            OthersMoney: paymentDetails.voucher,
            Voucher_ID: paymentDetails.voucherId,
            //CreditValue
          };
          setData(dataObjet);
          await handleSaveTransactionDetails(dataObjet);
          break;
        case ActionTypeEnum.TransactionRefund:
          await handleClearControls(false);
          // @ts-ignore
          const modifyTransactionObject: TransactionDetailResponseModel =
            request.request;
          setTransactionTypeState(modifyTransactionObject.TransactionType);
          if (
            modifyTransactionObject.TransactionDetaillist !== null &&
            modifyTransactionObject.TransactionDetaillist !== undefined &&
            modifyTransactionObject.TransactionDetaillist.length !== 0
          ) {
            modifyTransactionObject.TransactionDetaillist.forEach((row) => {
              row = handleUpdateExistingTransactionValues(row);
              return { ...row };
            });
          }
          const balance: number = await getAccountBalance(
            modifyTransactionObject.Customer_ID !== null &&
              modifyTransactionObject.Customer_ID !== undefined
              ? modifyTransactionObject.Customer_ID
              : 0,
            modifyTransactionObject.Currency_ID !== null &&
              modifyTransactionObject.Currency_ID !== undefined
              ? modifyTransactionObject.Currency_ID
              : null,
            beneficiaryList
          );
          const dataObject = handleTransactionTotalValues(
            modifyTransactionObject
          );
          setData({
            ...dataObject,
            balance: balance,
            TransactionDetaillist: [...dataObject.TransactionDetaillist],
          });
          //setTransactionTypeState(modifyTransactionObject.TransactionType);
          setGuidKey(generateGuid());
          break;
        case ActionTypeEnum.AddItem:
          await handleAddItem(
            request.request,
            transactionTypeState,
            userConfiguration
          );
          break;
      }
    };
    const handleAddItem = async (
      request: TransactionItemResponseModel,
      transactionType: DailyTransactionTypeEnum,
      userConfiguration: UserConfigurationModel
    ) => {
      request.Transaction_Type = Number(transactionType);
      request = await addItem(
        request,
        data,
        itemList && itemList.length > 0 ? itemList : itemsPerCategoryList,
        userConfiguration
      );
      const errorList: ValidationErrorModel[] = await validateAddItem(
        request,
        userConfiguration
      );
      if (errorList !== null && errorList.length !== 0) {
        onActionEvent({
          action: ActionTypeEnum.RaiseError,
          request: errorList,
        });
        return;
      } else {
        onActionEvent({
          action: ActionTypeEnum.ClearError,
        });
        //transactionType;
      }

      data.TransactionDetaillist = handleGroupItemsBasedOnId(
        data.TransactionDetaillist,
        request,
        userConfiguration
      );
      const dataObject = handleTransactionTotalValues(data);

      setData({
        ...dataObject,
        TransactionDetaillist: [...dataObject.TransactionDetaillist],
      });
      itemCategorySelectBoxMultiselectRef.current &&
        itemCategorySelectBoxMultiselectRef.current.clearValue();
      itemSelectBoxMultiselectRef.current &&
        itemSelectBoxMultiselectRef.current.clearValue();
      setTransactionItemObject({
        ...request,
        ItemDiscount: 0,
        ItemDiscountInCurrency:0,
        originalItemDiscountInCurrency:0,
        ItemDiscountRatio: 0,
        Quantity: 1,
      });
    };
    const handleSubmit = async (request: TransactionDetailResponseModel) => {
      request.TransactionType = transactionTypeState ?? transactionType;
      request.DueDate =
        request.PaymentType_ID == PaymentTypeEnum.Delay
          ? request.DueDate
          : null;
      request = UpdateTransactionActionUser(request);
      const errorList: ValidationErrorModel[] = await validateSubmitRequest(
        request,
        userConfiguration
      );
      switch (request.TransactionType) {
        case Number(DailyTransactionTypeEnum.SalesRefund):
        case Number(DailyTransactionTypeEnum.PurchasesRefund):
          const refundErrorList: ValidationErrorModel[] =
            await validateRefundSubmitRequestAdditionalValidation(request);
          if (refundErrorList !== null && refundErrorList.length !== 0) {
            errorList.push(...refundErrorList);
          }
          break;
      }
      if (errorList !== null && errorList.length !== 0) {
        onActionEvent({
          id: 0,
          action: ActionTypeEnum.RaiseError,
          request: errorList,
        });
        return;
      }
      switch (Number(data.PaymentType_ID)) {
        case Number(PaymentTypeEnum.Multiple):
          setShowPaymentDetailsModel(true);
          break;
        default:
          await handleSaveTransactionDetails(request);
          break;
      }
    };
    const handleClearComponentControls = async () => {
      currencySelectBoxMultiselectRef.current &&
        currencySelectBoxMultiselectRef.current.clearValue();
      storeEmployeeSelectBoxMultiselectRef.current &&
        storeEmployeeSelectBoxMultiselectRef.current.clearValue();
      costCenterSelectBoxMultiselectRef.current &&
        costCenterSelectBoxMultiselectRef.current.clearValue();
      paymentTypeSelectBoxMultiselectRef.current &&
        paymentTypeSelectBoxMultiselectRef.current.clearValue();
      paymentTypeForSupplierPaymentSelectBoxMultiselectRef.current &&
        paymentTypeForSupplierPaymentSelectBoxMultiselectRef.current.clearValue();
      calcTypeSelectBoxMultiselectRef.current &&
        calcTypeSelectBoxMultiselectRef.current.clearValue();
      customerSelectBoxMultiselectRef.current &&
        customerSelectBoxMultiselectRef.current.clearValue();
      itemCategorySelectBoxMultiselectRef.current &&
        itemCategorySelectBoxMultiselectRef.current.clearValue();
      StoreSelectBoxMultiselectRef.current &&
        StoreSelectBoxMultiselectRef.current.clearValue();
      itemSelectBoxMultiselectRef.current &&
        itemSelectBoxMultiselectRef.current.clearValue();
    };

    const handleClearControls = async (isResetRequestState: boolean = true) => {
      currencySelectBoxMultiselectRef.current &&
        currencySelectBoxMultiselectRef.current.clearValue();
      storeEmployeeSelectBoxMultiselectRef.current &&
        storeEmployeeSelectBoxMultiselectRef.current.clearValue();
      costCenterSelectBoxMultiselectRef.current &&
        costCenterSelectBoxMultiselectRef.current.clearValue();
      paymentTypeSelectBoxMultiselectRef.current &&
        paymentTypeSelectBoxMultiselectRef.current.clearValue();
      paymentTypeForSupplierPaymentSelectBoxMultiselectRef.current &&
        paymentTypeForSupplierPaymentSelectBoxMultiselectRef.current.clearValue();
      calcTypeSelectBoxMultiselectRef.current &&
        calcTypeSelectBoxMultiselectRef.current.clearValue();
      customerSelectBoxMultiselectRef.current &&
        customerSelectBoxMultiselectRef.current.clearValue();
      itemCategorySelectBoxMultiselectRef.current &&
        itemCategorySelectBoxMultiselectRef.current.clearValue();
      StoreSelectBoxMultiselectRef.current &&
        StoreSelectBoxMultiselectRef.current.clearValue();
      itemSelectBoxMultiselectRef.current &&
        itemSelectBoxMultiselectRef.current.clearValue();
      if (isResetRequestState) {
        setData({ ...initialValues });
      }
      setTransactionItemObject({ ...transactionItemInitialValues });
    };
    const handleSaveTransactionDetails = async (
      request: TransactionDetailResponseModel
    ) => {
      setLoading(true);
      const result = await saveTransaction(request);

      if (
        result.Errors !== null &&
        result.Errors !== undefined &&
        result.Errors.length !== 0
      ) {
        onActionEvent({
          id: 0,
          action: ActionTypeEnum.RaiseError,
          request: result.Errors,
        });
      } else {
        onActionEvent({
          action: ActionTypeEnum.AddSuccess,
        });
      }
      setLoading(false);
    };
    //#endregion
    //#region formik
    const formik = useFormik({
      initialValues: initialValues,
      //enableReinitialize: true,
      onReset: async () => {
        onActionEvent({
          id: 0,
          action: ActionTypeEnum.Clear,
        });
      },
      onSubmit: async () => {
        await handleSubmit(data);
      },
    });
    //#endregion
    //#region useEffect
    useEffect(() => {
      const handleResize = () => {
        setIsMobile(window.innerWidth < 600);
      };

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);
    useEffect(() => {
      setPageName(getPageNameByUrl());
    }, [isArabicCurrentLanguage()]);
    useEffect(() => {
      if (actionType !== ComponentActionTypeEnum.None) {
        const fillData = async () => {
          setActionType(ComponentActionTypeEnum.None);
          switch (actionType) {
            case ComponentActionTypeEnum.Clear:
              await handleClearControls(true);
              break;
            case ComponentActionTypeEnum.Add:
              break;
            case ComponentActionTypeEnum.Edit:
              const safeRequestCopy = request
                ? {
                    ...request,
                    TransactionDetaillist: request.TransactionDetaillist
                      ? [...request.TransactionDetaillist]
                      : [],
                  }
                : null;

              await handleClearControls(false);
              const modifyTransactionObject: TransactionDetailResponseModel =
                safeRequestCopy!; // request;
              if (modifyTransactionObject?.TransactionDetaillist?.length > 0) {
                modifyTransactionObject.TransactionDetaillist.forEach((row) => {
                  return {
                    ...row,
                    ...handleUpdateExistingTransactionValues(row),
                  };
                });
              }
              const balance: number = await getAccountBalance(
                modifyTransactionObject.Customer_ID != null
                  ? modifyTransactionObject.Customer_ID
                  : 0,
                modifyTransactionObject.Currency_ID != null
                  ? modifyTransactionObject.Currency_ID
                  : null,
                beneficiaryList
              );
              const dataObject = handleTransactionTotalValues(
                modifyTransactionObject
              );
              setData((prevData) => {
                return {
                  ...dataObject,
                  balance: balance,
                  TransactionDetaillist: [...dataObject.TransactionDetaillist],
                };
              });
              break;
            case ComponentActionTypeEnum.Refund:
              // @ts-ignore
              setRefundTransactionData(request);
              break;
            case ComponentActionTypeEnum.Reset:
              setLoading(true);
              await handleClearControls(true);
              //@ts-ignore
              await initializeTransactionValuesAndUserTransactionsConfiguration(
                setData,
                request!,
                [
                  { key: "customers", value: beneficiaryList },
                  { key: "payments", value: paymentTypeList },
                  { key: "currencies", value: currencyList },
                  { key: "employees", value: storeEmployeeList },
                  { key: "calcTypes", value: calcTypeList },
                  { key: "costCenters", value: costCenterList },
                ],
                userConfiguration.userTransactionSetting //,"key1"
              );
              setTransactionTypeState(transactionType);
              setTransactionItemObject(transactionItemInitialValues);
              setLoading(false);
              break;
            case ComponentActionTypeEnum.IssueTransactionFromSource:
              await handleClearComponentControls();
              setTransactionItemObject({ ...transactionItemInitialValues });
              setData({ ...request! });
              updateAllSelectBoxes(request!);
              break;
            default:
              break;
          }
        };
        fillData().then(() => {});
      }
    }, [actionType]);
    useEffect(() => {
      setShowRefundItemModel(
        refundTransactionData !== null &&
          refundTransactionData.TransactionDetaillist !== null &&
          refundTransactionData.TransactionDetaillist.length !== 0
      );
    }, [refundTransactionData]);
    //#endregion
    //#region html
    return (
      <div>
        {" "}
        {/*key={forceRerender} */}
        {(loading || !isTransactionLoaded) && <LoadingBox />}
        <form onSubmit={formik.handleSubmit}>
          {loading && <LoadingBox />}
          {/*<div*/}
          {/*    className="sticky-buttons-container"*/}
          {/*    style={{*/}
          {/*      // position: "sticky",*/}
          {/*      top: 0,*/}
          {/*      zIndex: 1000,*/}
          {/*      padding: "10px", // Ensures spacing*/}
          {/*      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",*/}
          {/*      backgroundColor: "transparent", // Makes background transparent*/}
          {/*      backdropFilter: "blur(5px)", // Optional: adds a slight blur effect for better readability*/}
          {/*    }}*/}
          {/*>*/}
          {/*  <div*/}
          {/*      className="card p-3 flex-sm-row flex-column d-flex align-items-sm-end align-items-center gap-4 justify-content-space-between mb-2 flex-wrap">*/}
          {/*    <div*/}
          {/*        className="col-md-10 col-12 row row-cols-1 row-cols-xxl-3 row-cols-xl-2 row-cols-lg-2 row-cols-md-2 row-cols-sm-1 g-md-4 g-sm-4">*/}
          {/*      <TextBox*/}
          {/*          inputName="totalMoneyInCurrency"*/}
          {/*          labelName={getLabelName("Total")}*/}
          {/*          inputValue={data.TotalMonyInCurrency}*/}
          {/*          formatType={"decimal"}*/}
          {/*          isReadOnly={true}*/}
          {/*      />*/}
          {/*      {userConfiguration.companySetting.ApplyTax && (*/}
          {/*          <TextBox*/}
          {/*              labelName={getLabelName("Tax_Value")}*/}
          {/*              inputValue={data.TotalTaxInCurrency}*/}
          {/*              inputName="taxValueInCurrency"*/}
          {/*              isReadOnly={true}*/}
          {/*          />*/}
          {/*      )}*/}
          {/*      <TextBox*/}
          {/*          inputName="netBillInCurrency"*/}
          {/*          labelName={getLabelName("Net Bill")}*/}
          {/*          inputValue={data.TotalNetInCurrency}*/}
          {/*          isReadOnly={true}*/}
          {/*      />*/}
          {/*    </div>*/}
          {/*    <div className="col-12 col-md-2 gap-3 d-flex justify-content-end">*/}
          {/*      {data.TransactionDetaillist.length > 0 && userConfiguration.userTransactionSetting.EnableButonAdd && (*/}
          {/*          <PrivilegesChecker*/}
          {/*              formId={formId}*/}
          {/*              action={data.ID === 0 ? "EnableSave" : "EnableUpdate"}*/}
          {/*          >*/}
          {/*            <ButtonBox*/}
          {/*                iconType="content-save"*/}
          {/*                type="submit"*/}
          {/*                className="btn btn-sm btn-gradient-primary"*/}
          {/*            >*/}
          {/*              {getLabelName(`${data.ID === 0 ? "Save" : "Update"}`)}*/}
          {/*            </ButtonBox>*/}
          {/*          </PrivilegesChecker>*/}
          {/*      )}*/}
          {/*      <ButtonBox*/}
          {/*          iconType="receipt"*/}
          {/*          variant="danger"*/}
          {/*          type="button"*/}
          {/*          className="btn btn-sm"*/}
          {/*          onClick={formik.handleReset}*/}
          {/*      >*/}
          {/*        {getLabelName("New")}*/}
          {/*      </ButtonBox>*/}
          {/*    </div>*/}
          {/*  </div>*/}
          {/*</div>*/}
          {isVisible && (
            <div
              className="sticky-buttons-container"
              style={{
                position: "sticky",
                top: 60,
                zIndex: 1,
                padding: "15px",
                marginBottom: "6px",
                background: "rgb(249, 247, 247)",
                boxShadow: "0px 10px 15px rgba(28, 26, 26, 0.1)",
                borderRadius: "16px",
                border: "1px solid  #e3e3e3",
              }}
            >
              <div className="d-flex flex-lg-nowrap flex-wrap justify-content-between align-items-center gap-3 w-100 p-1">
                {/* <div className="row flex-grow-1 w-100 align-items-center g-1 gap-1"> */}
                <div className="d-flex align-items-center gap-2 flex-grow-1">
                  <label className="fw-bold">{getLabelName("Total")}</label>
                  <TextBox
                    labelName=""
                    inputName="totalMoneyInCurrency"
                    inputValue={data.TotalMonyInCurrency}
                    formatType="decimal"
                    isReadOnly={true}
                  />
                </div>
                <div className="d-flex align-items-center gap-2 flex-grow-1">
                  <label className="fw-bold">{getLabelName("Tax_Value")}</label>
                  <TextBox
                    labelName=""
                    inputName="taxValueInCurrency"
                    inputValue={data.TotalTaxInCurrency}
                    isReadOnly={true}
                  />
                </div>
                <div className="d-flex align-items-center gap-2 flex-grow-1">
                  <label className="fw-bold">{getLabelName("Net Bill")}</label>
                  <TextBox
                    labelName=""
                    inputName="netBillInCurrency"
                    inputValue={data.TotalNetInCurrency}
                    isReadOnly={true}
                  />
                </div>
                <div className="d-flex align-items-center gap-2 flex-grow-1">
                  {data.TransactionDetaillist.length > 0 && (
                    <PrivilegesChecker
                      formId={formId}
                      action={data.ID === 0 ? "EnableSave" : "EnableUpdate"}
                    >
                      <ButtonBox
                        iconType="content-save"
                        type="submit"
                        className="btn btn-sm btn-gradient-primary"
                      >
                        {getLabelName(`${data.ID === 0 ? "Save" : "Update"}`)}
                      </ButtonBox>
                    </PrivilegesChecker>
                  )}
                  <ButtonBox
                    iconType="receipt"
                    variant="danger"
                    type="button"
                    className="btn btn-sm"
                    onClick={formik.handleReset}
                  >
                    {getLabelName("New")}
                  </ButtonBox>
                  {isMobile && (
                    <button
                      onClick={() => setIsVisible(false)}
                      className="btn btn-danger btn-sm ms-auto"
                    >
                      Close
                    </button>
                  )}
                </div>
                {/* </div> */}
              </div>
            </div>
          )}
          {!isVisible && isMobile && (
            <div
              onClick={() => setIsVisible(true)}
              style={{
                position: "fixed",
                bottom: "30px",
                right: "20px",
                backgroundColor: "#fe7c96",
                color: "#fff",
                borderRadius: "50%",
                width: "50px",
                height: "50px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                cursor: "pointer",
                zIndex: 2000,
              }}
              title="Show Summary Box"
            >
              +
            </div>
          )}

          {/*transaction header*/}
          <Accordion defaultActiveKey="0">
            <Accordion.Item eventKey="0">
              <Accordion.Header>{pageName}</Accordion.Header>
              <Accordion.Body>
                <RegisterTransactionHeader
                  formik={formik}
                  setUploadedFiles={setUploadedFiles}
                  uploadedFiles={uploadedFiles}
                  data={data}
                  transactionItemObject={{
                    ...transactionItemObject,
                    storeId: hasSingleStore
                      ? defaultStoreId
                      : transactionItemObject.storeId ?? null,
                  }}
                  userConfiguration={userConfiguration}
                  transactionConfiguration={{
                    transactionType: transactionTypeState,
                    isSalesComponent: isSalesComponent,
                    hasSingleStore: hasSingleStore,
                    defaultStoreId: defaultStoreId,
                  }}
                  lookups={{
                    beneficiaryList: beneficiaryList,
                    originalBeneficiaryList: _.cloneDeep(beneficiaryList),
                    paymentTypeList: paymentTypeList,
                    currencyList: currencyList,
                    storeEmployeeList: storeEmployeeList,
                    calcTypeList: calcTypeList,
                    costCenterList: costCenterList,
                    itemList: itemList ?? [],
                  }}
                  lookupRefs={{
                    currencySelectBoxMultiselectRef:
                      currencySelectBoxMultiselectRef,
                    paymentTypeSelectBoxMultiselectRef:
                      paymentTypeSelectBoxMultiselectRef,
                    paymentTypeForSupplierPaymentSelectBoxMultiselectRef:
                      paymentTypeForSupplierPaymentSelectBoxMultiselectRef,
                    costCenterSelectBoxMultiselectRef:
                      costCenterSelectBoxMultiselectRef,
                    calcTypeSelectBoxMultiselectRef:
                      calcTypeSelectBoxMultiselectRef,
                    storeEmployeeSelectBoxMultiselectRef:
                      storeEmployeeSelectBoxMultiselectRef,
                    customerSelectBoxMultiselectRef:
                      customerSelectBoxMultiselectRef,
                  }}
                  states={{
                    setData: setData,
                    setTransactionItemObject: setTransactionItemObject,
                    setBeneficiaryList: setBeneficiaryList,
                  }}
                  shouldReset={actionType == ComponentActionTypeEnum.Reset}
                  // onActionEvent={(o: RequestActionModel) => {
                  //   handleAction(o).then(() => {});
                  // }}
                />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          {/*register item*/}
          {!isRefundTransaction(transactionType) && (
            <Accordion defaultActiveKey="1">
              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  <span>
                    {" ".concat(getLabelName("Add Items To"), pageName)}
                  </span>
                </Accordion.Header>
                <Accordion.Body>
                  {transactionType !=
                    DailyTransactionTypeEnum.ExtractAndDistribute}{" "}
                  <RegisterTransactionItem
                    data={data}
                    transactionConfiguration={{
                      transactionType: transactionTypeState,
                      isSalesComponent: isSalesComponent,
                      hasSingleStore: hasSingleStore,
                    }}
                    lookups={{
                      itemList: itemList ?? [],
                      itemsPerCategoryList: itemsPerCategoryList ?? [],
                      categoryList: categoryList ?? [],
                      calcTypeList: calcTypeList ?? [],
                      storeList: storeList ?? [],
                    }}
                    lookupRefs={{
                      itemCategorySelectBoxMultiselectRef:
                        itemCategorySelectBoxMultiselectRef,
                      itemSelectBoxMultiselectRef: itemSelectBoxMultiselectRef,
                      StoreSelectBoxMultiselectRef:
                        StoreSelectBoxMultiselectRef,
                    }}
                    transactionItemObject={{
                      ...transactionItemObject,
                      rowGuid: generateGuid(),
                      // storeId: Number(storeList?.[0]?.value) || 0
                    }}
                    setTransactionItemObject={setTransactionItemObject}
                    userConfiguration={userConfiguration}
                    setItemList={setItemList}
                    setItemPerCategoryList={setItemPerCategoryList}
                    onActionEvent={(o: RequestActionModel) => {
                      handleAction(o).then(() => {});
                    }}
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          )}
          {/*transaction items list*/}
          {activeTransactionDetails.length > 0 && (
            <Accordion defaultActiveKey="2">
              <Accordion.Item eventKey="2">
                <Accordion.Body>
                  <>
                    <div className="item-unit-list">
                      <div className="section-title">
                        <span>{getLabelName("Item Transaction")}</span>
                      </div>
                      <GridTable
                        //key={`grid-${generateGuid()}`}
                        columns={columns}
                        data={activeTransactionDetails}
                        totalRows={1000}
                        currentPage={1}
                        pageSize={10000}
                        paginationType="none"
                        conditionalRowStyles={handleRowStyle(
                          activeTransactionDetails || []
                        )}
                      />
                    </div>
                  </>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          )}
          {/*payment Details*/}
          <ModelDialogBox
            isXCloseButtonVisible={true}
            isModelVisible={showPaymentDetailsModel}
            isCloseButtonVisible={false}
            isEscapeCloseEnabled={true}
            size="lg"
            title={getLabelName("payment details")}
            onCloseEvent={() => {
              setShowPaymentDetailsModel(false);
            }}
          >
            <PaymentDetails
              setShowPaymentDetailsModel={setShowPaymentDetailsModel}
              totalPayment={data.TotalNet}
              onActionEvent={(o: RequestActionModel) => {
                handleAction(o).then(() => {});
              }}
            />
          </ModelDialogBox>
          {showRefundItemModel && (
            <RefundItemList
              request={refundTransactionData}
              onActionEvent={(o: RequestActionModel) => {
                setShowRefundItemModel(false);
                handleAction(o).then(() => {});
              }}
            />
          )}
          <ButtonBox
            style={{ display: "none" }}
            ref={transactionSettingStatusButtonRef}
            onClick={async () => {}}
          />
        </form>
      </div>
    );
    //#endregion
  }
);
